import React, { useState, useEffect, useMemo } from "react";
import { Divider, Button, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";

import { actions as zoneManagementActions } from "../../redux/resources/zoneManagement";
import { useHooks } from "./hooks/useHooks";

// Import statistics and filters
import ZoneStatistics from "./components/ZoneStatistics";
import ZoneFilters from "./components/ZoneFilters";
import CreateZoneModal from "./components/CreateZoneModal";

import "./zoneManagement.less";

import CustomDivider from "../../components/Divider";
import { getUserTenant } from "../../helpers/authStorage";
import Map from "./components/Map";
import { ReactComponent as WarehouseIcon } from "./components/assets/Warehouse.svg";
import WarehouseDrawer from "./components/WarehouseDrawer";

const ZoneManagement = ({
  isMobile,
  getZones,
  postZones,
  patchZones,
  delZones,
  putWarehouseZones,
  postWarehouseZones,
  delWarehouseZones,
  getWarehouseZones,
  zoneWarehouseZones,
  updateZoneStateZones,
  updateZoneNameZones,
}) => {
  const zones = useSelector(({ zones }) => zones.item || []);
  
  const {
    warehouses,
    loadZones,
    handleAddZones,
    handleUpdateZones,
    handleDeleteZones,
    loadWarehouses,
    handleAddWarehouse,
    handleDeleteWarehouse,
    handleUpdateWarehouse,
    handleZoneWarehouseChange,
    handleUpdateZoneName,
  } = useHooks({
    getZones,
    postZones,
    patchZones,
    delZones,
    putWarehouseZones,
    postWarehouseZones,
    delWarehouseZones,
    getWarehouseZones,
    zoneWarehouseZones,
    updateZoneStateZones,
    updateZoneNameZones,
  });
  
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(-1);
  const [activeFilter, setActiveFilter] = useState("all");
  
  // CreateZoneModal state
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [drawnPolygon, setDrawnPolygon] = useState(null);

  const handleSetSelectedZoneIndex = (index) => (event) => setSelectedZoneIndex(index);

  const handleSetDrawerVisible = (value) => {
    setDrawerVisible(value);
  };

  // Calculate filtered zones
  const filteredZones = useMemo(() => {
    if (!zones || zones.length === 0) {
      return [];
    }
    
    if (activeFilter === "all") {
      return zones;
    } else if (activeFilter === "capital") {
      return zones.filter(zone => zone.isCapitalZone === true);
    } else if (activeFilter === "outside") {
      return zones.filter(zone => zone.isCapitalZone === false);
    }
    
    return zones;
  }, [zones, activeFilter]);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!zones || zones.length === 0) {
      return { total: 0, capital: 0, outside: 0 };
    }
    
    const capitalCount = zones.filter(zone => zone.isCapitalZone === true).length;
    const outsideCount = zones.filter(zone => zone.isCapitalZone === false).length;
    
    return {
      total: zones.length,
      capital: capitalCount,
      outside: outsideCount
    };
  }, [zones]);

  const handleFilterChange = (filter) => {
    console.log("Filter changed to:", filter);
    setActiveFilter(filter);
  };

  // Handle polygon drawn on map
  const handlePolygonDrawn = (polygon) => {
    console.log('✅ Polygon drawn from map:', polygon);
    setDrawnPolygon(polygon);
    setCreateModalVisible(true);
  };

  // NEW: Open modal manually (without drawing)
  const handleOpenCreateModal = () => {
    console.log('📝 Opening create modal manually');
    setDrawnPolygon(null); // No polygon from map
    setCreateModalVisible(true);
  };

  // Handle zone creation from modal
  const handleCreateZone = async (zoneData) => {
    console.log('✅ Creating zone with data:', zoneData);
    
    try {
      // Call the Redux action to create zone
      await handleAddZones(zoneData);
      
      // Success feedback
      console.log('✅ Zone created successfully!');
      
      // Reset and close modal
      setCreateModalVisible(false);
      setDrawnPolygon(null);
      
      // Reload zones to show the new one
      loadZones();
    } catch (error) {
      console.error('❌ Failed to create zone:', error);
    }
  };

  // Handle modal cancel
  const handleCancelCreate = () => {
    console.log('❌ Zone creation cancelled');
    setCreateModalVisible(false);
    setDrawnPolygon(null);
  };

  useEffect(() => {
    console.log('Loading zones and warehouses...');
    loadZones();
    loadWarehouses();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="pageWrapper">
        {/* Statistics Cards */}
        <ZoneStatistics 
          total={statistics.total}
          capital={statistics.capital}
          outside={statistics.outside}
        />

        {/* Filter Buttons */}
        <ZoneFilters 
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          total={statistics.total}
          capital={statistics.capital}
          outside={statistics.outside}
        />

        <h2 className="pageTitle">Zone Management</h2>
        <CustomDivider />
        
        <Divider />
        
        {/* Action Buttons */}
        <Space size="middle" style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleOpenCreateModal}
            size="large"
          >
            Create New Zone
          </Button>
          
          <Button onClick={() => handleSetDrawerVisible(true)}>
            Warehouse Management
          </Button>
        </Space>
        
        <Divider />
        
        {/* Map with polygon callback */}
        <Map
          drawerVisible={drawerVisible}
          handleAddZones={handleAddZones}
          handleUpdateZones={handleUpdateZones}
          handleDeleteZones={handleDeleteZones}
          zones={filteredZones}
          warehouses={warehouses}
          selectedZoneIndex={selectedZoneIndex}
          setSelectedZoneIndex={handleSetSelectedZoneIndex}
          handleZoneWarehouseChange={handleZoneWarehouseChange}
          handleUpdateZoneName={handleUpdateZoneName}
          getUserTenant={getUserTenant}
          onPolygonComplete={handlePolygonDrawn}
        />
        
        <WarehouseDrawer
          data={warehouses}
          isMobile={isMobile}
          drawerVisible={drawerVisible}
          setDrawerVisible={handleSetDrawerVisible}
          handleAddWarehouse={handleAddWarehouse}
          handleDeleteWarehouse={handleDeleteWarehouse}
          handleUpdateWarehouse={handleUpdateWarehouse}
        />

        {/* CreateZoneModal */}
        <CreateZoneModal
          visible={createModalVisible}
          polygon={drawnPolygon}
          onCancel={handleCancelCreate}
          onCreate={handleCreateZone}
          tenantName={getUserTenant()}
        />
      </div>
    </>
  );
};

export const WarehouseIconWrap = (props) => (
  <div className="warehouseMarkerContainer">
    <WarehouseIcon {...props} />
    <span
      style={{
        wordWrap: "normal",
        textAlign: "center",
        whiteSpace: "nowrap",
        color: "#01cdfb",
        textStroke: "1px black",
        background: "black",
        borderRadius: "5px",
        padding: "0px 3px",
        fontSize: props.isMobile ? "large" : "x-small",
      }}
    >
      {props.text}
    </span>
  </div>
);

const MSTP = (state) => ({
  isMobile: state.admin.isMobile,
});

export default connect(MSTP, (dispatch) => ({
  ...bindActionCreators(zoneManagementActions, dispatch),
}))(ZoneManagement);